import Cookies from 'universal-cookie'

import {
  IdentityNamespace,
  resolveIdentityNamespace,
} from '@lyrahealth-inc/shared-app-logic/src/features/authentication/identityNamespace'
import {
  HYBRID_LOGIN_POPULATION,
  REGISTRATION_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { REGISTRATION_QUERY_PARAMS } from '@lyrahealth-inc/shared-app-logic/src/features/registration/constants'
import type { PendingSsoIdentity } from '@lyrahealth-inc/shared-app-logic/src/models/login/Login'

import { getParentDomain } from './cookieUtils'
import { PARTNER_SSO_REGISTRATION_DATA_COOKIE, PENDING_SSO_INTERSTITIAL_COOKIE } from '../constants/appConstants'

const SSO_COOKIE_MAX_AGE_SECONDS = 60 * 15
const PENDING_SSO_INTERSTITIAL_COOKIE_MAX_AGE_SECONDS = 60

// Shared by every cookie set and removed here, so a removal always matches the cookie it targets
const parentDomainCookieOptions = () => ({ path: '/', domain: getParentDomain() })

const setParentDomainCookie = (name: string, value: string, maxAge: number) => {
  new Cookies().set(name, value, { ...parentDomainCookieOptions(), maxAge, secure: true, sameSite: 'lax' })
}

export type SsoRegistrationData = PendingSsoIdentity & { emailModified?: boolean }

/**
 * Build query parameters for navigating to the SSO registration form from the SSO interstitial.
 */
export function buildSsoRegistrationParams(data: SsoRegistrationData): Record<string, string> {
  return {
    [REGISTRATION_QUERY_PARAMS.ID]: data.email,
    [REGISTRATION_QUERY_PARAMS.IS_SSO]: 'true',
    ...(data.ssoRegistrationVerificationKey && {
      [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_VERIFICATION_KEY_QUERY_PARAM]:
        data.ssoRegistrationVerificationKey,
    }),
    ...(data.partnerIdpShortName && {
      [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM]: data.partnerIdpShortName,
    }),
    ...(data.ssoRedirectPath && {
      [REGISTRATION_QUERY_PARAMS.REDIRECT]: data.ssoRedirectPath,
    }),
  }
}

/**
 * Set the cross-subdomain cookie with SSO user data (name, dob, connected service)
 * so the registration form can pre-fill fields.
 */
export function setSsoRegistrationDataCookie(data: SsoRegistrationData): void {
  if (
    !data.givenName &&
    !data.familyName &&
    !data.dob &&
    !data.externalUserId &&
    !data.connectedServiceType &&
    !data.emailModified
  ) {
    return
  }
  const cookieData = {
    firstName: data.givenName || '',
    lastName: data.familyName || '',
    ...(data.dob && { dob: data.dob }),
    ...(data.externalUserId && { externalUserId: data.externalUserId }),
    ...(data.connectedServiceType && { connectedServiceType: data.connectedServiceType }),
    ...(data.emailModified && { emailModified: true }),
  }
  setParentDomainCookie(PARTNER_SSO_REGISTRATION_DATA_COOKIE, JSON.stringify(cookieData), SSO_COOKIE_MAX_AGE_SECONDS)
}

/**
 * Hand the pending SSO identity to the customer microsite, where Redux state doesn't carry over. The whole identity
 * is included: the interstitial page pre-fills from it and needs the verification key to build registration params.
 */
export function setPendingSsoInterstitialCookie(identity: PendingSsoIdentity): void {
  setParentDomainCookie(
    PENDING_SSO_INTERSTITIAL_COOKIE,
    JSON.stringify(identity),
    PENDING_SSO_INTERSTITIAL_COOKIE_MAX_AGE_SECONDS,
  )
}

/** Whether the member registering changed the email their SSO sign-in provided; false when the cookie is gone. */
export function getSsoRegistrationEmailModified(): boolean {
  return new Cookies().get(PARTNER_SSO_REGISTRATION_DATA_COOKIE)?.emailModified === true
}

export function getPendingSsoInterstitialCookie(): PendingSsoIdentity | null {
  return (new Cookies().get(PENDING_SSO_INTERSTITIAL_COOKIE) as PendingSsoIdentity | undefined) ?? null
}

export function clearPendingSsoInterstitialCookie(): void {
  new Cookies().remove(PENDING_SSO_INTERSTITIAL_COOKIE, parentDomainCookieOptions())
}

/** Maps backend member context to Mixpanel's hybrid-login property names. */
export const getHybridLoginTrackingProperties = ({
  emailModified,
  isLyraConnectUser,
  isInternational,
}: {
  emailModified?: boolean
  isLyraConnectUser?: boolean
  isInternational?: boolean
}) => ({
  ...(emailModified !== undefined && { [REGISTRATION_PROPERTIES.EMAIL_MODIFIED]: emailModified }),
  ...(isLyraConnectUser !== undefined && {
    [REGISTRATION_PROPERTIES.IS_LYRA_CONNECT_USER]: isLyraConnectUser,
  }),
  ...(isInternational !== undefined && {
    [REGISTRATION_PROPERTIES.POPULATION]: isInternational ? HYBRID_LOGIN_POPULATION.OUS : HYBRID_LOGIN_POPULATION.US,
  }),
})

/**
 * Whether two emails match, ignoring case and surrounding whitespace. Decides whether the interstitial can skip the
 * code step, so it treats two empty emails as the same rather than guessing.
 */
export const isSameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * Whether the email submitted at registration differs from the email the SSO sign-in provided, for analytics.
 * False when either email is unknown.
 */
export const isSsoEmailModified = (originalSsoEmail: unknown, submittedEmail?: string): boolean =>
  typeof originalSsoEmail === 'string' && !!submittedEmail && !isSameEmail(originalSsoEmail, submittedEmail)

/**
 * Origin hosting the SSO registration iframe. Lyra customers use the shared `care.` host; Bend Health
 * serves it from its own domain, so the current origin is used as-is.
 */
export function getSsoRegistrationOrigin(origin: string, customerSubdomain: string, customerName: string): string {
  return resolveIdentityNamespace(customerName) === IdentityNamespace.BendHealth
    ? origin
    : origin.replace(customerSubdomain + '.', 'care.')
}
